"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FEATURE_SCOPES = exports.CORE_SCOPES = exports.IG_LOGIN_SCOPE_COMMENTS = exports.IG_LOGIN_SCOPE_MESSAGES = exports.THREAD_PLATFORM_MESSAGE = exports.THREADS_MENTION_SCOPE = exports.THREADS_REPLY_SCOPE = exports.THREAD_MAX_POSTS = exports.POST_NEEDS_TEXT_MESSAGE = exports.socialPlatformLabel = exports.SOCIAL_MEDIA_RULES = exports.DOCUMENT_EXCLUSIVE_MESSAGE = exports.MEDIA_MIXING_MESSAGE = exports.INSTAGRAM_IMAGE_LIMIT = exports.X_IMAGE_LIMIT = exports.DEFAULT_IMAGE_LIMIT = exports.SOCIAL_PLATFORM_CHAR_LIMITS = exports.SOCIAL_PLATFORM_ORDER = void 0;
exports.socialCharLimit = socialCharLimit;
exports.charLimitViolation = charLimitViolation;
exports.socialImageLimit = socialImageLimit;
exports.socialMediaRules = socialMediaRules;
exports.fitMediaToPlatform = fitMediaToPlatform;
exports.mediaCompositionOf = mediaCompositionOf;
exports.mediaRuleViolation = mediaRuleViolation;
exports.postTextOf = postTextOf;
exports.supportsThreads = supportsThreads;
exports.hasScope = hasScope;
exports.hasThreadReplyScope = hasThreadReplyScope;
exports.hasThreadMentionScope = hasThreadMentionScope;
exports.threadPostsOf = threadPostsOf;
exports.isThread = isThread;
exports.threadStructureViolation = threadStructureViolation;
exports.threadPostViolations = threadPostViolations;
exports.threadPublishViolation = threadPublishViolation;
exports.threadHelperText = threadHelperText;
exports.featuresIn = featuresIn;
exports.scopesFor = scopesFor;
exports.optionalFeaturesOf = optionalFeaturesOf;
exports.instagramLoginType = instagramLoginType;
const char_count_1 = require("./char-count");
const enums_1 = require("./enums");
/**
 * Social connections — a Brand Workspace connects social accounts (the
 * Social Media Feeds config page, "Connections" in the menu) to publish to them
 * and to read their messages and analytics. Connections are tenant-scoped (per
 * Brand Workspace).
 */
/**
 * The platforms shown on the Social Media Feeds config page, in display order.
 * (SocialPlatform also carries YOUTUBE_SHORTS, used elsewhere but not here.)
 */
exports.SOCIAL_PLATFORM_ORDER = [
    enums_1.SocialPlatform.LINKEDIN,
    enums_1.SocialPlatform.FACEBOOK,
    enums_1.SocialPlatform.INSTAGRAM,
    enums_1.SocialPlatform.THREADS,
    enums_1.SocialPlatform.X,
];
/**
 * The HARD post-length cap each platform enforces, in characters.
 *
 * Deliberately separate from {@link AiLengthGuideline}: those are admin-editable *targets* that
 * steer AI generation, so a workspace lowering one to 150 must not start refusing saves. These are
 * the platforms' own limits — facts about the destination, not preferences — and they gate the
 * composer's Save/Publish.
 *
 * YOUTUBE_SHORTS is absent on purpose: it takes video, has no text post to measure, and publishing
 * to it isn't supported. A platform with no entry simply shows no counter and is never gated.
 */
exports.SOCIAL_PLATFORM_CHAR_LIMITS = {
    [enums_1.SocialPlatform.FACEBOOK]: 63_206,
    [enums_1.SocialPlatform.X]: 280,
    [enums_1.SocialPlatform.LINKEDIN]: 3_000,
    [enums_1.SocialPlatform.INSTAGRAM]: 2_200,
    [enums_1.SocialPlatform.THREADS]: 500,
};
/**
 * The character cap that applies to a post's text, or null when none does — a non-social channel
 * (Blog/Video/Podcast have no platform limit), or a platform we don't cap.
 */
function socialCharLimit(platform) {
    return (platform && exports.SOCIAL_PLATFORM_CHAR_LIMITS[platform]) ?? null;
}
/**
 * How far a post's text is over its platform's cap, counted the way that platform counts
 * ({@link countPostChars}), or null when it fits or no cap applies.
 *
 * The one length rule for the composer's counters and gates, copying between destinations and the
 * server's publish check - three places counting three ways is how a post passes here and fails at
 * the platform.
 */
function charLimitViolation(platform, text) {
    const limit = socialCharLimit(platform);
    if (limit == null)
        return null;
    const count = (0, char_count_1.countPostChars)(platform, text);
    return count > limit ? { count, limit } : null;
}
/**
 * How many images one post may carry as a gallery, per destination.
 *
 * Enforced in the COMPOSER, so a post can never be built that its destination would refuse — the
 * publisher re-checks as a backstop and refuses rather than silently dropping images.
 *
 * Instagram is 10, NOT 20: Instagram raised the limit to 20 in the app, but the Content Publishing
 * API is still documented as "Carousels are limited to 10 images, videos, or a mix of the two" and
 * takes "up to 10 container IDs". Allowing 20 would build every child container and only then fail
 * on the final publish. Re-check Meta's content-publishing docs before raising this.
 */
exports.DEFAULT_IMAGE_LIMIT = 20;
exports.X_IMAGE_LIMIT = 4;
exports.INSTAGRAM_IMAGE_LIMIT = 10;
/** The image cap for a destination. Non-social channels get the default — they aren't gallery-posted. */
function socialImageLimit(platform) {
    return platform ? exports.SOCIAL_MEDIA_RULES[platform].maxImages : exports.DEFAULT_IMAGE_LIMIT;
}
/**
 * What an author is told when photos and video would end up in the same social post — the product
 * owner's wording, shown at the moment of choosing and again at the publish gate.
 */
exports.MEDIA_MIXING_MESSAGE = 'Social media platforms do not allow videos and photos to be posted simultaneously over their integrations. Please select either all video or all photos.';
/**
 * Why a PDF can't share a post with photos or video: LinkedIn's document post carries the one
 * document and nothing else (a post's `content` is a single media id).
 */
exports.DOCUMENT_EXCLUSIVE_MESSAGE = 'A PDF carousel is the whole attachment of a LinkedIn post. Remove the photos or video, or remove the PDF.';
/**
 * Per-destination composition rules.
 *
 * Facts about the platforms, like {@link SOCIAL_PLATFORM_CHAR_LIMITS} and unlike the admin-editable
 * AI targets — so they gate the composer rather than merely steering it.
 *
 * NO SOCIAL DESTINATION MIXES PHOTOS AND VIDEO. The owner ruled it, superseding the earlier call to
 * let X take "1 video + up to 3 images" the way its own apps do: delivery has only ever sent the
 * video when a post holds one, on every platform, so photos beside it were accepted here and then
 * silently never published. A post is all photos or all video, and the author is told so in
 * MEDIA_MIXING_MESSAGE the moment they try.
 *
 * Instagram is 10, not 20 — see DEFAULT_IMAGE_LIMIT above; the Content Publishing API takes ten
 * container ids however many the app allows.
 *
 * LinkedIn's 20 is OUR default rather than a documented LinkedIn cap; nobody has confirmed the real
 * number. It is deliberately unchanged from what the image limit already allowed, so this table
 * loosens nothing.
 */
exports.SOCIAL_MEDIA_RULES = {
    [enums_1.SocialPlatform.INSTAGRAM]: {
        maxItems: exports.INSTAGRAM_IMAGE_LIMIT,
        maxImages: exports.INSTAGRAM_IMAGE_LIMIT,
        // ONE, though a carousel can hold ten: delivery sends a single video per post (videoAsset takes
        // the first and a second "has nowhere to go"), so a second one would be accepted here and then
        // silently never published. Raise it with multi-video delivery, not before.
        maxVideos: 1,
        maxDocuments: 0,
        allowMixing: false,
        singleTextBlock: true,
        singleMediaBlock: true,
    },
    [enums_1.SocialPlatform.THREADS]: {
        maxItems: 10,
        maxImages: 10,
        // ONE, for the same reason as Instagram: only the first video of a post is ever delivered.
        maxVideos: 1,
        maxDocuments: 0,
        allowMixing: false,
        // One text box and one media panel PER POST. A thread is several posts in a row, and each of
        // them is held to this on its own — see the note on X below.
        singleTextBlock: true,
        singleMediaBlock: true,
    },
    [enums_1.SocialPlatform.X]: {
        maxItems: exports.X_IMAGE_LIMIT,
        maxImages: exports.X_IMAGE_LIMIT,
        maxVideos: 1,
        maxDocuments: 0,
        allowMixing: false,
        // X and Threads can carry a thread — several posts in a row (see supportsThreads). These two
        // flags are the rule for EACH post of one: a thread post holds one text box and one media
        // panel, exactly like a post that stands alone, and threadPostViolations checks it post by
        // post. A thread is more posts, never more boxes inside a post, so these stay `true`.
        singleTextBlock: true,
        singleMediaBlock: true,
    },
    [enums_1.SocialPlatform.FACEBOOK]: {
        maxItems: exports.DEFAULT_IMAGE_LIMIT,
        maxImages: exports.DEFAULT_IMAGE_LIMIT,
        maxVideos: 1,
        maxDocuments: 0,
        allowMixing: false,
        singleTextBlock: true,
        singleMediaBlock: true,
    },
    [enums_1.SocialPlatform.LINKEDIN]: {
        maxItems: exports.DEFAULT_IMAGE_LIMIT,
        maxImages: exports.DEFAULT_IMAGE_LIMIT,
        maxVideos: 1,
        // A PDF, shown as a carousel of its pages (the Documents API) - one per post, and the whole
        // attachment: see DOCUMENT_EXCLUSIVE_MESSAGE.
        maxDocuments: 1,
        allowMixing: false,
        singleTextBlock: true,
        singleMediaBlock: true,
    },
    [enums_1.SocialPlatform.YOUTUBE_SHORTS]: {
        maxItems: 1,
        maxImages: 0,
        maxVideos: 1,
        maxDocuments: 0,
        allowMixing: false,
        singleTextBlock: true,
        singleMediaBlock: true,
    },
};
/** The rules for a destination, or null for a non-social channel, which has none. */
function socialMediaRules(platform) {
    return platform ? exports.SOCIAL_MEDIA_RULES[platform] : null;
}
/**
 * Trim one run of media to what a destination accepts, instead of refusing it.
 *
 * "Apply same media to set" pushes ONE gallery onto destinations whose rules differ, and the ask is
 * that each takes as much of it as it can rather than none — six images is an Instagram carousel
 * and too many for X, a video beside photos is fine on Threads and refused by LinkedIn. So:
 *
 * - A destination that will not mix photos and video takes the VIDEO and drops the photos.
 * - Over a per-kind cap, the first pieces in the author's order are kept.
 * - Over the total cap, photos give way before the video, the same priority as the mixing rule.
 *
 * Kept pieces stay in the source's order. The result always satisfies {@link mediaRuleViolation}'s
 * media checks for that destination; it may be EMPTY (a destination that takes none of it), and the
 * caller must say so rather than write an empty attachment over what the destination had.
 */
function fitMediaToPlatform(platform, media) {
    const rules = socialMediaRules(platform);
    if (!rules)
        return [...media];
    const videos = media.filter((m) => m.kind === 'VIDEO');
    const images = !rules.allowMixing && videos.length > 0 ? [] : media.filter((m) => m.kind === 'IMAGE');
    const keptVideos = videos.slice(0, rules.maxVideos);
    const room = Math.max(0, rules.maxItems - keptVideos.length);
    const keptImages = images.slice(0, Math.min(rules.maxImages, room));
    const keep = new Set([...keptVideos, ...keptImages]);
    return media.filter((m) => keep.has(m));
}
const isMediaKind = (kind) => kind === 'IMAGE' || kind === 'VIDEO';
/**
 * The composition of a set of content pieces, in the terms the rules are written in.
 *
 * Shared because the rules are now checked in two places that hold content in different shapes: the
 * composer's editor blocks in the browser, and Content rows on the server when copying one
 * destination's content onto another. Counting them differently is how a limit ends up enforced in
 * the UI and not at the write, or the reverse.
 *
 * FIELDS, not items: a run of consecutive media is one media field, and an empty text piece is not
 * a field the user has used.
 *
 * A run of media in ANY mix is one field, because that is what the composer shows as one Media panel
 * on a social post. Counting a video as a field of its own — as this once did — made one visible
 * panel holding a video and three photos read as two fields, so a one-panel rule would refuse X's
 * deliberate "1 video + up to 3 images" with a message about panels the author cannot see.
 */
function mediaCompositionOf(pieces) {
    let images = 0;
    let videos = 0;
    let documents = 0;
    let textFields = 0;
    let mediaFields = 0;
    // Whether the current run of media has been counted. Set by the first piece in a run that holds
    // an asset; only a NON-media piece ends a run. An empty placeholder tile is part of the panel it
    // sits in, so it must neither open a field nor stop the real piece after it from being counted —
    // a video dropped into a fresh panel lands right after the empty image tile "Add media" created.
    let runCounted = false;
    for (const p of pieces) {
        if (!isMediaKind(p.kind))
            runCounted = false;
        if (p.kind === 'TEXT') {
            if (p.text?.trim())
                textFields += 1;
        }
        else if (p.assetId) {
            if (p.kind === 'IMAGE')
                images += 1;
            if (p.kind === 'VIDEO')
                videos += 1;
            if (p.kind === 'DOCUMENT')
                documents += 1;
            if (isMediaKind(p.kind) && !runCounted) {
                mediaFields += 1;
                runCounted = true;
            }
            else if (!isMediaKind(p.kind)) {
                // AUDIO, a DOCUMENT and anything else: a piece of its own, never part of a media panel.
                mediaFields += 1;
            }
        }
    }
    return { images, videos, documents, textFields, mediaFields };
}
/**
 * Why this composition breaks the destination's rules, or null when it doesn't.
 *
 * One function so the add-controls, the save gate, the publish gate and any server-side backstop
 * cannot drift into disagreeing — five places enforcing four slightly different rules is how a
 * limit becomes advisory.
 */
function mediaRuleViolation(platform, composition) {
    const rules = socialMediaRules(platform);
    if (!rules)
        return null;
    const name = exports.socialPlatformLabel[platform];
    const { images, videos, documents, textFields, mediaFields } = composition;
    // A PDF first: its rules are about the KIND of post, and the generic one-media-field rule below
    // would otherwise answer a PDF beside photos with a message about fields.
    if (documents > 0) {
        if (rules.maxDocuments === 0) {
            return `${name} can't take a PDF. PDF carousels post to LinkedIn only.`;
        }
        if (documents > rules.maxDocuments) {
            return `${name} takes one PDF per post.`;
        }
        if (images > 0 || videos > 0) {
            return exports.DOCUMENT_EXCLUSIVE_MESSAGE;
        }
    }
    if (!rules.allowMixing && images > 0 && videos > 0) {
        return exports.MEDIA_MIXING_MESSAGE;
    }
    if (videos > rules.maxVideos) {
        return rules.maxVideos === 0
            ? `${name} doesn't accept video in this kind of post.`
            : `${name} takes ${rules.maxVideos === 1 ? 'one video' : `${rules.maxVideos} videos`} per post.`;
    }
    if (images > rules.maxImages) {
        return `${name} takes up to ${rules.maxImages} images per post.`;
    }
    if (images + videos > rules.maxItems) {
        return `${name} takes up to ${rules.maxItems} media items per post.`;
    }
    if (rules.singleTextBlock && textFields > 1) {
        return `${name} posts take a single text field.`;
    }
    if (rules.singleMediaBlock && mediaFields > 1) {
        return `${name} posts take a single media field.`;
    }
    return null;
}
/** Human label for a platform (UI may override icons/colors). */
exports.socialPlatformLabel = {
    [enums_1.SocialPlatform.LINKEDIN]: 'LinkedIn',
    [enums_1.SocialPlatform.FACEBOOK]: 'Facebook',
    [enums_1.SocialPlatform.INSTAGRAM]: 'Instagram',
    [enums_1.SocialPlatform.THREADS]: 'Threads',
    [enums_1.SocialPlatform.X]: 'X / Twitter',
    [enums_1.SocialPlatform.YOUTUBE_SHORTS]: 'YouTube Shorts',
};
// ── A post's text ─────────────────────────────────────────────────────────
/**
 * Said wherever a social post with no text is about to be scheduled, queued, set to "post as soon
 * as approved", submitted for approval or published. A thread says it per post instead ("Post n
 * needs text.", {@link threadPostViolations}).
 */
exports.POST_NEEDS_TEXT_MESSAGE = 'This post has no text. Add some before you schedule or publish it.';
/**
 * A post's text: the trimmed text of the first TEXT piece that has any, or null when none has.
 *
 * Never the post's title (that is internal, and is never published), and never a PDF's title,
 * which rides on its DOCUMENT piece. Pass the pieces in the order they are stored (`order`,
 * `createdAt`, `id`): an empty first text box does not hide a filled second one.
 */
function postTextOf(pieces) {
    for (const piece of pieces) {
        if (piece.kind !== enums_1.ContentKind.TEXT)
            continue;
        const text = piece.text?.trim();
        if (text)
            return text;
    }
    return null;
}
// ── Threads (X and Threads: several posts published in a row) ─────────────
/** The most posts one thread may hold. */
exports.THREAD_MAX_POSTS = 50;
/**
 * The Threads permission that publishing a reply needs — and posts 2..N of a thread are replies to
 * the post before them. Not among the default connect scopes: a connection asks for it only when
 * thread publishing is turned on for it.
 */
exports.THREADS_REPLY_SCOPE = 'threads_manage_replies';
/**
 * The Threads permission that listing the posts which @mention the account (Messages) needs. Like
 * {@link THREADS_REPLY_SCOPE}, asked for only when it is turned on for a connection.
 */
exports.THREADS_MENTION_SCOPE = 'threads_manage_mentions';
/** Said wherever a thread meets a destination that cannot take one. */
exports.THREAD_PLATFORM_MESSAGE = 'A thread can only go to X or Threads.';
/** Whether a destination can carry a thread. X and Threads only. */
function supportsThreads(platform) {
    return platform === enums_1.SocialPlatform.X || platform === enums_1.SocialPlatform.THREADS;
}
/**
 * Whether a connection's stored scopes include `scope`.
 *
 * Scopes are stored the way each provider writes them — comma-separated for Meta's platforms,
 * space-separated for X — so both separators split. The whole name has to match: a longer scope
 * that merely starts with the same letters is not this one.
 */
function hasScope(scopes, scope) {
    if (!scope)
        return false;
    return (scopes ?? '').split(/[\s,]+/).includes(scope);
}
/** Whether a connection's stored scopes include {@link THREADS_REPLY_SCOPE}. See {@link hasScope}. */
function hasThreadReplyScope(scopes) {
    return hasScope(scopes, exports.THREADS_REPLY_SCOPE);
}
/** Whether a connection's stored scopes include {@link THREADS_MENTION_SCOPE}. See {@link hasScope}. */
function hasThreadMentionScope(scopes) {
    return hasScope(scopes, exports.THREADS_MENTION_SCOPE);
}
/**
 * The pieces grouped into thread posts: one array per `threadIndex`, lowest index first, each
 * keeping the order its pieces arrived in. Pass pieces already sorted by `order`.
 *
 * A post that is not a thread comes back as a single group, and no pieces as no groups.
 */
function threadPostsOf(pieces) {
    const byIndex = new Map();
    for (const piece of pieces) {
        const index = piece.threadIndex ?? 0;
        const post = byIndex.get(index);
        if (post)
            post.push(piece);
        else
            byIndex.set(index, [piece]);
    }
    return [...byIndex.entries()].sort(([a], [b]) => a - b).map(([, post]) => post);
}
/** Whether these pieces are a thread: some piece belongs to a post after the first. */
function isThread(pieces) {
    return pieces.some((piece) => (piece.threadIndex ?? 0) > 0);
}
/**
 * Why these pieces cannot be stored as a thread, or null when they can — and null at once when they
 * are not a thread at all: a single post is never judged here, so every post that existed before
 * threads passes untouched.
 *
 * This is about SHAPE only: which post each piece belongs to, where the thread is going, and what
 * kind of piece it holds. What a post needs before it can be PUBLISHED — text, length, media counts
 * — is {@link threadPostViolations}, because a draft may be saved with a post that is still empty
 * or too long.
 *
 * `pieces` in stored order. The first rule broken is the answer.
 */
function threadStructureViolation(platform, channel, pieces) {
    if (!isThread(pieces))
        return null;
    // Posts run 0, 1, 2… along the stored order: the list starts at 0, never steps back and never
    // skips a number. That is what lets a publisher send them in order and pick up again at "the
    // first post not yet live".
    let last = -1;
    for (const piece of pieces) {
        const index = piece.threadIndex ?? 0;
        if (!Number.isInteger(index) || index < 0 || index < last || index > last + 1) {
            return "This thread's posts are out of order. Reload the page and try again.";
        }
        last = index;
    }
    if (channel !== enums_1.ContentChannel.SOCIAL || !supportsThreads(platform)) {
        return 'This post is a thread, and only X and Threads can take a thread. Delete posts 2 onwards first.';
    }
    const posts = threadPostsOf(pieces);
    if (posts.length > exports.THREAD_MAX_POSTS) {
        return `A thread can have up to ${exports.THREAD_MAX_POSTS} posts.`;
    }
    for (const [i, post] of posts.entries()) {
        if (post.filter((piece) => piece.kind === enums_1.ContentKind.TEXT).length > 1) {
            return `Post ${i + 1} has more than one text box.`;
        }
    }
    if (pieces.some((piece) => piece.kind === enums_1.ContentKind.DOCUMENT || piece.kind === enums_1.ContentKind.AUDIO)) {
        return 'A thread post takes text, images or one video. Remove the PDF or audio.';
    }
    return null;
}
/**
 * What stops each post of a thread from being published: at most ONE violation per post, the first
 * of these that applies.
 *
 *  1. `empty`            neither text nor media
 *  2. `too-long`         text over the destination's character cap
 *  3. `too-many-images`  more images than the destination takes in one post
 *  4. `no-text`          media without text — a post's text is always required, and in a thread
 *                        nothing stands in for it (the title is never sent)
 *  5. `media`            any other composition rule, in {@link mediaRuleViolation}'s words
 *
 * `posts` as {@link threadPostsOf} returns them. `from` skips the posts before it: the ones already
 * live, which can no longer change and are not sent again.
 *
 * The length is the stored text counted the way the destination counts it
 * ({@link charLimitViolation}: on X a link is 23 and an emoji or a Chinese, Japanese or Korean
 * character is 2; on Threads an emoji is its UTF-8 bytes) — the same number the composer's counter
 * shows, so the composer and the publish gate cannot disagree, not even over a trailing space. A
 * media piece with no file is a placeholder, not media.
 *
 * One function for the composer's gates, copying a thread between destinations and the publish
 * pre-flight, for the reason {@link mediaRuleViolation} is one function.
 */
function threadPostViolations(platform, posts, from = 0) {
    const label = platform ? exports.socialPlatformLabel[platform] : 'this destination';
    const imageLimit = socialImageLimit(platform);
    const count = (n) => n.toLocaleString('en-US');
    // A `from` that is not a whole number of posts skips nothing: a gate must never let a post
    // through because it was handed a bad count.
    const start = Number.isInteger(from) && from > 0 ? from : 0;
    const violations = [];
    for (let i = start; i < posts.length; i += 1) {
        const post = posts[i];
        const n = i + 1;
        const text = post.find((piece) => piece.kind === enums_1.ContentKind.TEXT)?.text ?? '';
        const hasText = text.trim() !== '';
        const composition = mediaCompositionOf([...post]);
        // Any filled piece that is not text. An empty tile is not media, so a post holding only one is
        // still empty.
        const hasMedia = composition.mediaFields > 0;
        const over = charLimitViolation(platform, text);
        if (!hasText && !hasMedia) {
            violations.push({
                post: n,
                problem: 'empty',
                message: `Post ${n} is empty. Add text or media, or delete it.`,
            });
        }
        else if (over) {
            violations.push({
                post: n,
                problem: 'too-long',
                message: `Post ${n} is ${count(over.count)} characters, over ${label}'s ${count(over.limit)}-character limit.`,
            });
        }
        else if (composition.images > imageLimit) {
            violations.push({
                post: n,
                problem: 'too-many-images',
                message: `Post ${n} has ${count(composition.images)} images, more than the ${count(imageLimit)} ${label} accepts in one post.`,
            });
        }
        else if (!hasText) {
            violations.push({ post: n, problem: 'no-text', message: `Post ${n} needs text.` });
        }
        else {
            const breaks = mediaRuleViolation(platform, composition);
            if (breaks)
                violations.push({ post: n, problem: 'media', message: `Post ${n}: ${breaks}` });
        }
    }
    return violations;
}
/** The first thing that stops a thread from being published, as its sentence, or null. */
function threadPublishViolation(platform, posts, from = 0) {
    return threadPostViolations(platform, posts, from)[0]?.message ?? null;
}
/**
 * The line under a thread saying what each of its posts may hold, or null for a destination that
 * takes no thread.
 */
function threadHelperText(platform) {
    if (!supportsThreads(platform))
        return null;
    return `Each post: up to ${socialCharLimit(platform)} characters, plus ${socialImageLimit(platform)} images or 1 video.`;
}
/** Instagram Login: the Direct messages of the Messages inbox. */
exports.IG_LOGIN_SCOPE_MESSAGES = 'instagram_business_manage_messages';
/** Instagram Login: reading and replying to comments and tags in the Messages inbox. */
exports.IG_LOGIN_SCOPE_COMMENTS = 'instagram_business_manage_comments';
/**
 * What a first connect asks for, and what a connection always keeps: the code branches on some of
 * these (`pages_show_list` decides an Instagram account's sign-in), so a stored list never loses one.
 * Instagram here is Instagram Login; an Instagram account connected through Facebook keeps its own
 * Facebook Login list, outside this table.
 */
exports.CORE_SCOPES = {
    [enums_1.SocialPlatform.FACEBOOK]: [
        'public_profile',
        'pages_show_list',
        'pages_read_engagement',
        'pages_manage_posts',
    ],
    [enums_1.SocialPlatform.THREADS]: [
        'threads_basic',
        'threads_content_publish',
        'threads_read_replies',
        'threads_manage_insights',
    ],
    [enums_1.SocialPlatform.INSTAGRAM]: [
        'instagram_business_basic',
        'instagram_business_content_publish',
        'instagram_business_manage_insights',
    ],
};
/**
 * Each optional feature's scopes, per sign-in, in the order a request lists them (Threads: replies,
 * then mentions). A feature is ON only when ALL of its scopes are stored.
 */
exports.FEATURE_SCOPES = {
    [enums_1.SocialPlatform.FACEBOOK]: {
        messaging: ['pages_messaging'],
        engagement: ['pages_read_user_content', 'pages_manage_engagement'],
        insights: ['read_insights'],
    },
    [enums_1.SocialPlatform.THREADS]: {
        threadReplies: [exports.THREADS_REPLY_SCOPE],
        threadMentions: [exports.THREADS_MENTION_SCOPE],
    },
    [enums_1.SocialPlatform.INSTAGRAM]: {
        inbox: [exports.IG_LOGIN_SCOPE_MESSAGES, exports.IG_LOGIN_SCOPE_COMMENTS],
    },
};
function featureTableOf(platform) {
    return (platform && exports.FEATURE_SCOPES[platform]) || null;
}
/**
 * The optional features ON for a connection: those whose scopes are ALL in its stored list (one of
 * two is off). In table order; none for a platform without optional features.
 */
function featuresIn(platform, scopes) {
    const table = featureTableOf(platform);
    if (!table)
        return [];
    return Object.keys(table).filter((feature) => (table[feature] ?? []).every((scope) => hasScope(scopes, scope)));
}
/**
 * The scopes a sign-in asks for: the platform's {@link CORE_SCOPES}, then the scopes of each of
 * `features` that the platform has, in table order, without repeats. A feature of another platform
 * is ignored, so asking Instagram for Threads mentions asks for nothing more.
 */
function scopesFor(platform, features) {
    const scopes = [...exports.CORE_SCOPES[platform]];
    const table = exports.FEATURE_SCOPES[platform];
    for (const feature of Object.keys(table)) {
        if (!features.includes(feature))
            continue;
        for (const scope of table[feature] ?? []) {
            if (!scopes.includes(scope))
                scopes.push(scope);
        }
    }
    return scopes;
}
/**
 * The connection's optional features and whether each is on — the value of
 * {@link SocialConnection.optionalFeatures}. Keys only for the features that exist on this
 * connection: none for X or LinkedIn, and none for an Instagram account connected through Facebook.
 */
function optionalFeaturesOf(platform, scopes) {
    const table = featureTableOf(platform);
    if (!table)
        return {};
    if (platform === enums_1.SocialPlatform.INSTAGRAM && instagramLoginType(scopes) === 'facebook')
        return {};
    const on = featuresIn(platform, scopes);
    const states = {};
    for (const feature of Object.keys(table))
        states[feature] = on.includes(feature);
    return states;
}
/**
 * Which sign-in an INSTAGRAM connection was made with. Facebook Login stores a Page token and always
 * holds `pages_show_list`; Instagram Login never does — the test the publisher and analytics already
 * use. Nothing stored reads as Instagram Login, as it does for the publisher.
 */
function instagramLoginType(scopes) {
    return hasScope(scopes, 'pages_show_list') ? 'facebook' : 'instagram';
}
//# sourceMappingURL=social.js.map